import { useState, useEffect, useRef } from "react";
import axios from "axios";
import niwaLogo from "../assets/logo.png";
import { useNavigate } from "react-router-dom";
import { RefreshCw, Eye, EyeOff } from "lucide-react";

const API = "https://backapi.ypsispma.com/api";

const Login = () => {

  const [userCode, setUserCode] = useState("");
  const [password, setPassword] = useState("");

  const [captcha, setCaptcha] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");

  const [captchaExpiry, setCaptchaExpiry] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(60);
  const [captchaStartTime, setCaptchaStartTime] = useState(0);

  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const canvasRef = useRef(null);
  const navigate = useNavigate();

  // ================= CAPTCHA =================

  useEffect(() => {
    generateCaptcha();
  }, []);

  // countdown timer
  useEffect(() => {

    if (!captchaExpiry) return;

    const interval = setInterval(() => {

      const remaining =
        Math.max(0, Math.floor((captchaExpiry - Date.now()) / 1000));

      setSecondsLeft(remaining);

      if (remaining === 0) generateCaptcha();

    }, 1000);

    return () => clearInterval(interval);

  }, [captchaExpiry]);

  const generateCaptcha = () => {

    setCaptchaInput("");

    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let text = "";

    for (let i = 0; i < 5; i++)
      text += chars[Math.floor(Math.random() * chars.length)];

    setCaptcha(text);

    setTimeout(() => drawCaptcha(text), 0);

    setCaptchaExpiry(Date.now() + 60000);
    setCaptchaStartTime(Date.now());
  };

  const drawCaptcha = (text) => {

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = "#f1f5f9";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.font = "28px monospace";
    ctx.fillStyle = "#1e293b";

    // characters
    for (let i = 0; i < text.length; i++) {

      const x = 20 + i * 28;
      const y = 35;
      const angle = (Math.random() - 0.5) * 0.6;

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.fillText(text[i], 0, 0);
      ctx.restore();
    }

    // noise lines
    for (let i = 0; i < 6; i++) {
      ctx.beginPath();
      ctx.moveTo(Math.random()*170, Math.random()*50);
      ctx.lineTo(Math.random()*170, Math.random()*50);
      ctx.strokeStyle = "#cbd5e1";
      ctx.stroke();
    }

    // noise dots
    for (let i = 0; i < 40; i++) {
      ctx.fillRect(Math.random()*170, Math.random()*50, 2, 2);
    }
  };

  // ================= LOGIN =================

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    // captcha expiry
    if (Date.now() > captchaExpiry) {
      setErrorMsg("Captcha expired. Refresh and try again.");
      generateCaptcha();
      return;
    }

    // bot speed detection
    if (Date.now() - captchaStartTime < 1500) {
      setErrorMsg("Suspicious activity detected.");
      generateCaptcha();
      return;
    }

    // captcha validation
    if (
      captchaInput.trim().toUpperCase() !==
      captcha.trim().toUpperCase()
    ) {
      setErrorMsg("Invalid captcha");
      generateCaptcha();
      return;
    }

    try {

      setIsLoading(true);

      const res = await axios.post(`${API}/Login`, {
        username: userCode,
        password: password
      });

      if (!res.data?.token)
        throw new Error("Token not received");

      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      localStorage.setItem(
        "permissions",
        JSON.stringify(res.data.permissions)
      );

      navigate("/dashboard");

    } catch (err) {

      setErrorMsg(
        err.response?.data?.message || "Login failed"
      );

    } finally {

      setIsLoading(false);
      generateCaptcha(); // one-time captcha
    }
  };

  // ================= UI =================

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">

      <div className="absolute top-6 left-8">
        <img src={niwaLogo} alt="Logo" className="h-12"/>
      </div>

      <div className="flex flex-1">

        {/* LEFT PANEL */}
        <div className="hidden lg:flex w-1/2 bg-slate-900 text-white flex-col justify-center p-20">
          <div className="max-w-md">
            <h1 className="text-4xl font-black mb-5">
              Work Management System
            </h1>
            <p className="text-slate-300">
              Streamline assignments, monitor compliance deadlines,
              and improve operational visibility.
            </p>
          </div>
        </div>

        {/* LOGIN CARD */}
        <div className="flex-1 flex items-center justify-center p-8">

          <div className="bg-white shadow-xl rounded-[28px] p-10 w-full max-w-[440px] border">

            <h2 className="text-3xl font-bold text-center mb-8">
              Sign In
            </h2>

            {errorMsg && (
              <div className="mb-6 p-3 bg-rose-50 text-rose-600 rounded-lg text-sm font-semibold">
                {errorMsg}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">

              <input
                placeholder="User ID"
                value={userCode}
                onChange={(e)=>setUserCode(e.target.value)}
                className="w-full bg-slate-50 border rounded-lg px-5 py-4"
              />

              <div className="relative">
                <input
                  type={showPassword?"text":"password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e)=>setPassword(e.target.value)}
                  className="w-full bg-slate-50 border rounded-lg px-5 py-4"
                />
                <button
                  type="button"
                  onClick={()=>setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition"
                  >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {/* CAPTCHA */}
              <div className="space-y-2">

                <div className="flex items-center gap-3">
                  <canvas
                    ref={canvasRef}
                    width={170}
                    height={50}
                    className="border rounded-lg bg-slate-50"
                  />

                  <button
                    type="button"
                    onClick={generateCaptcha}
                    className="h-[50px] w-[50px] flex items-center justify-center border rounded-lg hover:bg-slate-100"
                  >
                    <RefreshCw size={18}/>
                  </button>
                </div>

                <input
                  placeholder="Enter Captcha"
                  value={captchaInput}
                  onChange={(e)=>setCaptchaInput(e.target.value)}
                  className="w-full bg-slate-50 border rounded-lg px-4 py-3"
                />

                <p className="text-xs text-slate-400">
                  Captcha expires in {secondsLeft}s
                </p>

              </div>

              <button
                disabled={isLoading}
                className="w-full bg-slate-900 text-white rounded-lg py-4 font-bold"
              >
                {isLoading ? "Signing..." : "Sign In"}
              </button>

            </form>

            <p className="text-xs text-center text-slate-400 mt-6">
              Authorized access only. Activity may be monitored.
            </p>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;